import React, { FunctionComponent } from 'react';
import { Money } from 'api';
import { RuleType } from 'ui/types/investment';
import Translate from 'ui/atoms/translate';
import Number from 'ui/atoms/number';
import Currency from 'ui/atoms/currency';
import { camelCase } from 'change-case';
import { CountryEnum, Product } from 'api';
import { ProductCampaignsStatus } from 'apps/issuer/pages/product-details/campaigns/shared/product-campaign-status';
import Date from 'ui/atoms/date';
import CardItem from 'src/ui/atoms/card-item';

export interface ProductCampaignCardProps {
  /** Additional classes. */
  className?: string;

  id: string;

  allocation: number;

  allocationAvailable: number;

  minNumberOfTokens: number;

  maxNumberOfTokens: number;

  pricePerToken: Money;

  ruleType: RuleType;

  country: CountryEnum;

  active?: boolean;

  token?: Product;

  name?: string | null;

  signedPercentage: number;

  createdAt: Date;

  distributionPlatform: string;
}

const ProductCampaignCard: FunctionComponent<ProductCampaignCardProps> = (props) => {
  const {
    id,
    className,
    allocation,
    minNumberOfTokens,
    maxNumberOfTokens,
    pricePerToken,
    ruleType,
    country,
    active,
    token,
    createdAt,
    signedPercentage,
    name,
    distributionPlatform,
  } = props;

  return (
    <div className={className}>
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.product" truncate={true} />}
        content={token?.name}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.campaignId" truncate={true} />}
        content={id}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.typeOfOffering" truncate={true} />}
        content={
          <Translate
            name={
              ruleType === RuleType.MinimumTicketSize
                ? `ruleType.minimumTicketSizeAlternative`
                : `ruleType.${camelCase(ruleType)}`
            }
          />
        }
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.reference" truncate={true} />}
        content={name}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.createdAt" truncate={true} />}
        content={<Date>{createdAt}</Date>}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.distributionPlatform" truncate={true} />}
        content={distributionPlatform}
      />
      <CardItem
        title={<Translate name="studioCampaignInvestmentCard.pricePerToken" truncate={true} />}
        content={<Currency decimals="*">{pricePerToken}</Currency>}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.allocation.title" truncate={true} />}
        content={
          <Translate
            name="issuerProductDetails.campaigns.campaignDetails.allocation.content"
            args={[(_, key) => <Number key={key}>{allocation}</Number>]}
          />
        }
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.investmentsRange" truncate={true} />}
        content={
          <Translate
            name="studioCampaignInvestmentCard.nrOfTokenRange.content"
            args={[
              (_, key) => <Number key={key}>{minNumberOfTokens}</Number>,
              (_, key) => <Number key={key}>{maxNumberOfTokens}</Number>,
            ]}
          />
        }
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.signed" truncate={true} />}
        content={`${signedPercentage.toFixed()}%`}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.market" truncate={true} />}
        content={<Translate name={`countries.${country}`} />}
      />
      <CardItem
        title={<Translate name="studioCampaignInvestmentCard.statusLabel" truncate={true} />}
        content={<ProductCampaignsStatus active={active} />}
      />
    </div>
  );
};

export default ProductCampaignCard;
